import Constants from "expo-constants";
import { isDeviceTablet } from "../utils/dimensions";

const TEST_ENV_URL = "http://test.ordering.albaikcloud.com";
const apiRoot = "api";
const apiVersion = "v1";
const tposApiVersion = "tpos";

const host =
  process.env.NODE_ENV === "test"
    ? TEST_ENV_URL
    : Constants.expoConfig?.extra?.apiHost;

type UrlEndPoint = {
  url: string;
  method: string;
  prefix?: boolean;
  handleUnauthorized?: boolean;
  apiVersion?: string;
};

type ListBranchesProps = {
  latitude: number;
  longitude: number;
  products: number[];
  requestedAt: number;
};

const listBranches = ({
  latitude,
  longitude,
  products,
  requestedAt,
}: ListBranchesProps): UrlEndPoint => {
  const productQuery = products.reduce(
    (acc, product) => `${acc}&products[]=${product}`,
    "",
  );

  const latitudeQuery =
    latitude && !isDeviceTablet ? `&latitude=${latitude}` : "";
  const longitudeQuery =
    longitude && !isDeviceTablet ? `&longitude=${longitude}` : "";

  return {
    url: `/branches?requested_at=${requestedAt}${productQuery}${latitudeQuery}${longitudeQuery}`,
    method: "GET",
    prefix: true,
  };
};

const listProducts = (page = 1): UrlEndPoint => ({
  method: "GET",
  url: `/products?page=${page}`,
  prefix: true,
  apiVersion: "v2",
});

const getOrder = (orderId: number): UrlEndPoint => ({
  method: "GET",
  url: `/orders/${orderId}`,
  prefix: true,
});

const deleteOrder = (orderId: number): UrlEndPoint => ({
  method: "DELETE",
  url: `/orders/${orderId}`,
});

const deletePaymentMethod = (paymentMethodId: number): UrlEndPoint => ({
  method: "DELETE",
  url: `/payment_methods/${paymentMethodId}`,
});

const smsValidation = (userId: number): UrlEndPoint => {
  return {
    method: "POST",
    url: `/users/${userId}/validation`,
    handleUnauthorized: false,
  };
};

const getVerifications = (telephone: number): UrlEndPoint => {
  return {
    method: "GET",
    url: `/verifications/${telephone}`,
    handleUnauthorized: false,
  };
};

const resendOtp = {
  method: "POST",
  url: "/verifications",
  handleUnauthorized: false,
};

const updateOrderOnGeoFenceChange = (orderId: number): UrlEndPoint => {
  return {
    method: "PUT",
    url: `/orders/${orderId}/customer_location`,
    handleUnauthorized: false,
  };
};

const getCheck3dsStatusAsync = (threeDSecureId: number): UrlEndPoint => {
  return {
    url: `/three_d_secure_check_statuses/${threeDSecureId}`,
    method: "GET",
  };
};

const getGroups = (id: number): UrlEndPoint => {
  return {
    url: `/groups/${id}`,
    method: "GET",
  };
};

const getPrefix = (prefix?: boolean) =>
  isDeviceTablet && prefix ? tposApiVersion : apiVersion;

export const getFullURL = (
  url: string,
  prefix?: boolean,
  customApiVersion?: string,
): string => {
  const apiPrefix = getPrefix(prefix);
  if (customApiVersion) {
    return `${host}/${apiRoot}/${customApiVersion}/${apiPrefix}${url}`;
  }
  return `${host}/${apiRoot}/${apiPrefix}${url}`;
};

const getAggregatorOrder = (aggregator: string): UrlEndPoint => {
  return {
    url: `/aggregators/${aggregator}/orders`,
    method: "POST",
    prefix: true,
  };
};

const customerArrival = (orderId: string): UrlEndPoint => {
  return {
    url: `/orders/${orderId}/customer_arrival`,
    method: "POST",
    prefix: true,
    apiVersion: "v2",
  };
};
const listOrders = (page: number): UrlEndPoint => {
  return {
    method: "GET",
    url: `/orders?page=${page}`,
    prefix: true,
  };
};

export default {
  config: { method: "GET", url: "/config", prefix: true },
  listOrders,
  createBasket: { method: "POST", url: "/baskets" },
  listProducts,
  listBranches,
  menuItems: {
    url: "/menu_items",
    method: "GET",
    prefix: true,
    apiVersion: "v2",
  },
  heroItems: { url: "/hero_items", method: "GET" },
  getGroups,
  groupOrder: {
    url: "/group_orders",
    method: "POST",
  },
  getAggregatorOrder,
  order: {
    url: "/orders",
    method: "POST",
    prefix: true,
  },
  deleteOrder,
  login: {
    url: "/sessions",
    method: "POST",
    handleUnauthorized: false,
    prefix: true,
  },
  register: { url: "/users", method: "POST", handleUnauthorized: false },
  forgotPassword: {
    url: "/passwords",
    method: "POST",
    handleUnauthorized: false,
  },
  me: {
    url: "/me",
    method: "GET",
    prefix: true,
  },
  createPaymentSession: { url: "/payment/sessions", method: "POST" },
  listPaymentMethod: { url: "/payment_methods", method: "GET" },
  savePaymentMethod: { url: "/payment_methods", method: "POST" },
  deletePaymentMethod,
  updateUser: { url: "/users", method: "PUT" },
  check3dsEnrollment: {
    url: "/payment/3d_secure/check",
    method: "POST",
    featureFlags: { "ALBAIK-Async-3D-Secure-Check": 1 },
  },
  getCheck3dsStatusAsync,
  appVersions: { url: "/app_versions", method: "GET" },
  smsValidation,
  resendOtp,
  getVerifications,
  updateOrderOnGeoFenceChange,
  featureFlags: { url: "/feature_flags", method: "GET" },
  getOrder,
  customerArrival,
};
